<template>
    <BModal
        id="des1-modal"
        v-model="modalShow"
        :no-close-on-backdrop="true"
        dialog-class="modal-dialog modal-lg"
        body-class="p-3"
        size="xl"
    >
        <template #header>
            <h2 class="m-0">Оцінка учасників тендера</h2>
            <div class="panel-toolbar pr-3 pb-2 align-self-end">
                <button class="btn btn-panel bg-transparent fs-xl w-auto h-auto rounded-0" @click="modalShow=false">
                    <i class="fal fa-times"></i>
                </button>
            </div>
        </template>
        
        <BTable
            v-if="rows.length"
            :items="rows"
            :fields="fields"
            bordered
            hover
            head-variant="light"
            responsive="sm"
            small
        >
            <!-- Контрагент -->
            <template #cell(company)="{ item }">
                {{ companyName(item.user) }}
            </template>
            
            <!-- Відповідає техзавданню -->
            <template #cell(tech)="{ index }">
                <BFormSelect v-model.number="rows[index].rating.tech" :options="ynOptions" size="sm" />
            </template>
            
            <!-- Надана вся інформація -->
            <template #cell(info_full)="{ index }">
                <BFormSelect v-model.number="rows[index].rating.info_full" :options="ynOptions" size="sm" />
            </template>
            
            <!-- Сумлінно приймав участь -->
            <template #cell(honesty)="{ index }">
                <div class="d-flex flex-column gap-1">
                    <BFormSelect v-model.number="rows[index].rating.honesty" :options="ynOptions" size="sm" />
                    <BFormInput
                        v-if="rows[index].rating.honesty === 0"
                        v-model.trim="rows[index].rating.honesty_problem"
                        size="sm"
                        placeholder="Опишіть суть проблеми"
                    />
                </div>
            </template>
            
            <!-- Умови договору відповідають заявці (лише переможець) -->
            <template #cell(contract_is_responsible)="{ item, index }">
                <BFormSelect
                    v-model.number="rows[index].rating.contract_is_responsible"
                    :options="ynOptions"
                    :disabled="!item.winner"
                    size="sm"
                />
            </template>
            
            <!-- Постачання виконано вчасно (лише переможець) -->
            <template #cell(supply_is_responsible)="{ item, index }">
                <BFormSelect
                    v-model.number="rows[index].rating.supply_is_responsible"
                    :options="ynOptions"
                    :disabled="!item.winner"
                    size="sm"
                />
            </template>
            
            <!-- Якість відповідає ТЗ (лише переможець) -->
            <template #cell(quality_is_responsible)="{ item, index }">
                <BFormSelect
                    v-model.number="rows[index].rating.quality_is_responsible"
                    :options="ynOptions"
                    :disabled="!item.winner"
                    size="sm"
                />
            </template>
        </BTable>
        
        <div v-else class="text-muted">Немає заявок</div>
        
        <template #footer>
            <BButton :disabled="!canPut" size="lg" variant="outline-primary" @click="saveRatio">
                Зберегти
            </BButton>
        </template>
    </BModal>
</template>

<script>
import {
    BModal,
    BTable,
    BFormSelect,
    BFormInput,
    BButton
} from 'bootstrap-vue-next'

export default {
    name: 'rating_contragent',
    components: { BModal, BTable, BFormSelect, BFormInput, BButton },
    
    props: {
        // Опціонально для назв компаній (якщо є protocol.members)
        protocol: { type: Object, required: false }
    },
    
    emits: ['override_finish'],
    
    data () {
        return {
            modalShow: false,
            tenderId: null,
            rows: [] // масив з /ratio: [{ user, winner, rating: {...} }, ...]
        }
    },
    
    created () {
        EventBus.$on('show_rating_protocol', this.show)
    },
    
    computed: {
        fields () {
            return [
                { key: 'company', label: 'Контрагент', class: 'text-nowrap', stickyColumn: true },
                { key: 'tech', label: 'Відповідає техзавданню' },
                { key: 'info_full', label: 'Надана вся інформація' },
                { key: 'honesty', label: 'Сумлінно приймав участь' },
                { key: 'contract_is_responsible', label: 'Умови договору відповідають заявці' },
                { key: 'supply_is_responsible', label: 'Постачання виконано вчасно' },
                { key: 'quality_is_responsible', label: 'Якість відповідає техзавданню' }
            ]
        },
        ynOptions () {
            return [
                { value: null, text: 'Без оцінки' },
                { value: 1, text: 'Так' },
                { value: 0, text: 'Ні' }
            ]
        },
        membersByAuthor () {
            const map = {}
            const members = this.protocol?.members || []
            members.forEach(m => { map[String(m.author_id)] = m })
            return map
        },
        canPut () {
            return this.rows.length > 0
        }
    },
    
    methods: {
        companyName (userId) {
            return this.membersByAuthor[String(userId)]?.company || `Користувач #${userId}`
        },
        
        toNumOrNull (v) {
            if (v === null || v === undefined || v === '') return null
            const n = Number(v)
            return Number.isNaN(n) ? null : n
        },
        
        async loadRatio (protocol_id) {
            this.rows = []
            try {
                const { data } = await api.get(`/tenders/sales/protocols/inside/${protocol_id}/ratio`)
                if (data?.status === 'operation_success' && Array.isArray(data.data)) {
                    this.rows = data.data.map(item => ({
                        user: item.user,
                        winner: !!item.winner,
                        rating: {
                            id: item.rating?.id ?? 0,
                            tender_id: item.rating?.tender_id ?? protocol_id,
                            tender_user_id: item.rating?.tender_user_id ?? 0,
                            tech: this.toNumOrNull(item.rating?.tech),
                            info_full: this.toNumOrNull(item.rating?.info_full),
                            honesty: this.toNumOrNull(item.rating?.honesty),
                            honesty_problem: item.rating?.honesty_problem || '',
                            contract_is_responsible: this.toNumOrNull(item.rating?.contract_is_responsible),
                            supply_is_responsible: this.toNumOrNull(item.rating?.supply_is_responsible),
                            quality_is_responsible: this.toNumOrNull(item.rating?.quality_is_responsible)
                        }
                    }))
                }
            } catch (e) {
                console.error('loadRatio error', e)
            }
        },
        
        async saveRatio () {
            const payload = {
                data: this.rows.map(r => ({
                    user: r.user,
                    winner: !!r.winner,
                    rating: {
                        id: r.rating?.id ?? 0,
                        tender_id: r.rating?.tender_id ?? this.tenderId ?? 0,
                        tender_user_id: r.rating?.tender_user_id ?? 0,
                        tech: this.toNumOrNull(r.rating?.tech),
                        info_full: this.toNumOrNull(r.rating?.info_full),
                        honesty: this.toNumOrNull(r.rating?.honesty),
                        honesty_problem: r.rating?.honesty === 0 ? (r.rating?.honesty_problem || '') : '',
                        contract_is_responsible: this.toNumOrNull(r.rating?.contract_is_responsible),
                        supply_is_responsible: this.toNumOrNull(r.rating?.supply_is_responsible),
                        quality_is_responsible: this.toNumOrNull(r.rating?.quality_is_responsible)
                    }
                }))
            }
            
            try {
                const { data } = await api.post(`/tenders/sales/protocols/inside/${this.tenderId}/ratio`, payload)
                if (data?.status === 'operation_success') {
                    this.$emit('override_finish')
                    this.modalShow = false
                } else {
                    this.modalShow = false
                    console.warn('Save ratio failed:', data)
                }
            } catch (e) {
                this.modalShow = false
                console.error('Save ratio error:', e)
            }
        },
        
        show (protocol_id) {
            this.tenderId = protocol_id
            this.modalShow = true
            this.loadRatio(protocol_id)
        }
    }
}
</script>

<style scoped>
/* опционально — мелкая косметика */
</style>
